import type { IdbStoreSchema } from "./_types";

/**
 * 선언된 스토어 집합과 실제 DB 상태를 맞추는 reconcile.
 *
 * Owns:
 * - 스토어 규칙. 선언에 없는 스토어는 버린다. 없는 스토어는 만든다. `keyPath` 가 달라진
 *   스토어는 버린 뒤 다시 만든다. `keyPath` 는 생성 뒤 바꿀 수 없으므로 다시 만드는 방법뿐이다.
 * - 계획과 적용의 분리. 무엇을 할지의 판단을 IDB 없이 검증할 수 있게 한다.
 *
 * Does not own:
 * - 업그레이드 트랜잭션을 여는 일과 그 실패 처리(`_connection`).
 * - 스토어 이름의 뜻. 선언은 주입된다.
 *
 * 데이터 보존은 목표가 아니다. 이 DB 는 캐시이므로 형태가 바뀐 스토어는 비우는 것이 맞다.
 * 그래서 "스토어 이름을 바꾸지 마라" 처럼 사람이 지켜야 하는 규약이 필요 없다.
 */

/**
 * 업그레이드 트랜잭션에서 읽은 현재 스토어 한 줄.
 *
 * `keyPath` 가 `null` 이면 out-of-line 키로 만든 스토어다. 이 계층은 이 형태를 만들 수 없다.
 * 그래서 선언과 같을 수 없고, 항상 다시 만드는 대상이다.
 */
export type ExistingStore = {
  name: string;
  keyPath: string | readonly string[] | null;
};

export type SchemaPlan = {
  drop: readonly string[];
  // `keyPath` 가 달라진 스토어는 `drop` 과 `create` 양쪽에 들어간다.
  create: readonly IdbStoreSchema[];
};

/** 현재 상태와 선언을 대조해 업그레이드 안에서 할 일을 계산한다. IDB 에 접근하지 않는다. */
export function planSchemaChanges(
  existing: readonly ExistingStore[],
  declared: readonly IdbStoreSchema[],
): SchemaPlan {
  const declaredByName = new Map(declared.map((store) => [store.name, store]));
  const existingByName = new Map(existing.map((store) => [store.name, store]));

  const drop = existing
    .filter((store) => {
      const wanted = declaredByName.get(store.name);
      return wanted === undefined || !hasSameKeyPath(store.keyPath, wanted.keyPath);
    })
    .map((store) => store.name);

  const create = declared.filter((store) => {
    const current = existingByName.get(store.name);
    return current === undefined || !hasSameKeyPath(current.keyPath, store.keyPath);
  });

  return { drop, create };
}

/**
 * 계획을 적용한다. **`onupgradeneeded` 안에서 동기로만 호출한다.** 여기서 무엇이든 기다리면
 * 업그레이드 트랜잭션이 커밋된다. 그러면 그 뒤의 생성 작업이 실행되지 않는다.
 */
export function applySchemaPlan(db: IDBDatabase, plan: SchemaPlan): void {
  for (const name of plan.drop) {
    db.deleteObjectStore(name);
  }
  for (const store of plan.create) {
    // lib.dom 의 keyPath 는 readonly 배열을 받지 않는다. 그래서 복사해 전달한다.
    const keyPath = typeof store.keyPath === "string" ? store.keyPath : [...store.keyPath];
    db.createObjectStore(store.name, { keyPath });
  }
}

/**
 * 현재 스토어와 그 `keyPath` 를 읽는다. 이름만으로는 형태가 바뀌었는지 알 수 없다.
 * 그래서 업그레이드 트랜잭션으로 각 스토어를 열어야 한다.
 */
export function readExistingStores(
  db: IDBDatabase,
  transaction: IDBTransaction,
): readonly ExistingStore[] {
  return Array.from(db.objectStoreNames, (name) => ({
    name,
    keyPath: transaction.objectStore(name).keyPath,
  }));
}

// compound key 는 순서가 정렬 기준이다. 그래서 이름이 같아도 순서가 다르면 다른 스토어다.
function hasSameKeyPath(
  left: string | readonly string[] | null,
  right: string | readonly string[],
): boolean {
  if (left === null) {
    return false;
  }
  if (typeof left === "string" || typeof right === "string") {
    return left === right;
  }
  return left.length === right.length && left.every((segment, index) => segment === right[index]);
}
